import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Checkbox,
  Container,
  Header,
  Icon,
  Loader,
  Message,
  Segment,
} from 'semantic-ui-react';
import { API, showError } from '../../helpers';
import Charts from './Charts';
import FailureTable from './FailureTable';
import FilterPanel from './FilterPanel';
import RankingPanels from './RankingPanels';
import SummaryCards from './SummaryCards';
import {
  buildDetailUrl,
  buildStatsPageParams,
  buildStatsParams,
  createDefaultFilters,
  materializeRelativeRange,
} from './utils';
import './ErrorLogStats.css';

const AUTO_REFRESH_MS = 60 * 1000;

const ErrorLogStats = () => {
  const navigate = useNavigate();
  const initialFilters = useMemo(
    () => createDefaultFilters(window.location.search),
    []
  );
  const [filters, setFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stale, setStale] = useState(false);
  const [initialError, setInitialError] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const latestRequestId = useRef(0);
  const hasSuccessfulData = useRef(false);
  const requestAbortController = useRef(null);

  const fetchStats = useCallback(async (currentFilters, quiet = false) => {
    if (quiet && requestAbortController.current) return;
    if (!quiet && requestAbortController.current) {
      requestAbortController.current.abort();
    }
    const abortController = new AbortController();
    requestAbortController.current = abortController;
    const requestFilters = materializeRelativeRange(currentFilters);
    const requestId = ++latestRequestId.current;
    if (!quiet) {
      setLoading(true);
      if (!hasSuccessfulData.current) setInitialError('');
    }
    try {
      const params = buildStatsParams(requestFilters);
      const response = await API.get(`/api/error_log/stats?${params}`, {
        signal: abortController.signal,
        suppressGlobalError: true,
        timeout: 17 * 1000,
      });
      if (!response?.data) {
        throw new Error('统计请求未返回有效响应');
      }
      if (!response.data.success) {
        throw new Error(response.data.message || '统计加载失败');
      }
      if (requestId === latestRequestId.current) {
        setStats(response.data.data);
        setLastUpdated(new Date());
        setStale(false);
        setInitialError('');
        hasSuccessfulData.current = true;
        if (requestFilters.range_seconds) {
          setFilters((current) =>
            Number(current.range_seconds) ===
            Number(currentFilters.range_seconds)
              ? {
                  ...current,
                  start_timestamp: requestFilters.start_timestamp,
                  end_timestamp: requestFilters.end_timestamp,
                }
              : current
          );
        }
      }
    } catch (error) {
      if (error.code === 'ERR_CANCELED' || error.name === 'CanceledError') {
        return;
      }
      if (error.response?.status === 401) {
        showError(error);
        return;
      }
      if (requestId === latestRequestId.current) {
        const message = error.message || '错误日志统计加载失败';
        if (hasSuccessfulData.current) {
          setStale(true);
          showError(message);
        } else {
          setInitialError(message);
          setStale(false);
        }
      }
    } finally {
      if (requestAbortController.current === abortController) {
        requestAbortController.current = null;
        setLoading(false);
      }
    }
  }, []);

  useEffect(
    () => () => {
      const abortController = requestAbortController.current;
      requestAbortController.current = null;
      abortController?.abort();
    },
    []
  );

  useEffect(() => {
    fetchStats(appliedFilters);
  }, [appliedFilters, fetchStats]);

  useEffect(() => {
    if (!autoRefresh) return undefined;
    const timer = window.setInterval(
      () => fetchStats(appliedFilters, true),
      AUTO_REFRESH_MS
    );
    return () => window.clearInterval(timer);
  }, [appliedFilters, autoRefresh, fetchStats]);

  const applyFilters = () => {
    const nextFilters = materializeRelativeRange(filters);
    setFilters(nextFilters);
    setAppliedFilters(nextFilters);
    const params = buildStatsPageParams(nextFilters);
    window.history.replaceState(null, '', `/error_log/stats?${params}`);
  };

  const resetFilters = () => {
    const defaults = createDefaultFilters('');
    setFilters(defaults);
    setAppliedFilters(defaults);
    window.history.replaceState(null, '', '/error_log/stats');
  };

  const drillDown = (overrides) =>
    navigate(
      buildDetailUrl(materializeRelativeRange(appliedFilters), overrides)
    );

  const refreshStats = () => {
    const nextFilters = materializeRelativeRange(appliedFilters);
    setFilters(nextFilters);
    setAppliedFilters(nextFilters);
    const params = buildStatsPageParams(nextFilters);
    window.history.replaceState(null, '', `/error_log/stats?${params}`);
  };

  return (
    <Container fluid className='error-log-stats-page'>
      <div className='error-stats-page-header'>
        <div>
          <Header as='h2'>
            <Icon name='warning sign' />
            <Header.Content>
              错误日志统计
              <Header.Subheader>
                实时监控失败组合，并一键定位到日志明细
              </Header.Subheader>
            </Header.Content>
          </Header>
        </div>
        <div className='error-stats-header-actions'>
          <span className='error-stats-updated'>
            最后更新：
            {lastUpdated
              ? lastUpdated.toLocaleTimeString('zh-CN', { hour12: false })
              : '—'}
          </span>
          <Checkbox
            toggle
            label='60 秒自动刷新'
            checked={autoRefresh}
            onChange={(_, { checked }) => setAutoRefresh(checked)}
          />
          <Button icon labelPosition='left' onClick={refreshStats}>
            <Icon name={loading ? 'spinner' : 'refresh'} loading={loading} />
            {loading ? '重新加载' : '刷新'}
          </Button>
        </div>
      </div>

      <Segment className='error-stats-filter-panel'>
        <FilterPanel
          filters={filters}
          onChange={setFilters}
          onSearch={applyFilters}
          onReset={resetFilters}
          loading={loading}
        />
      </Segment>

      {stale && (
        <Message warning>
          <Message.Header>统计数据可能已过期</Message.Header>
          <p>本次刷新失败，页面保留上一次成功数据。</p>
        </Message>
      )}

      {initialError && !stats ? (
        <Message negative>
          <Message.Header>错误日志统计加载失败</Message.Header>
          <p>{initialError}</p>
          <Button onClick={refreshStats}>重试</Button>
        </Message>
      ) : loading && !stats ? (
        <Segment className='error-stats-loading'>
          <Loader active inline='centered'>
            正在聚合错误日志
          </Loader>
        </Segment>
      ) : (
        <>
          <SummaryCards summary={stats?.summary} />
          <Charts
            trend={stats?.trend}
            bucketUnit={stats?.bucket_unit}
            requestTypes={stats?.request_types}
            statusCodes={stats?.status_codes}
            onRequestTypeClick={(requestType) =>
              drillDown({ request_type: requestType })
            }
            onStatusCodeClick={(statusCode) =>
              drillDown({ status_code: statusCode })
            }
          />
          <FailureTable
            rows={stats?.top_failures}
            onDrillDown={(row) =>
              drillDown({
                request_type: row.request_type,
                status_code: row.status_code,
                failure_code: row.failure_code,
              })
            }
          />
          <RankingPanels
            models={stats?.top_models}
            channels={stats?.top_channels}
            onModelClick={(modelName) => drillDown({ model_name: modelName })}
            onChannelClick={(channelId) => drillDown({ channel_id: channelId })}
          />
        </>
      )}
    </Container>
  );
};

export default ErrorLogStats;
